import { create } from 'zustand'

/** Shared tool processing lifecycle. */
export type ToolStatus = 'idle' | 'selected' | 'processing' | 'done' | 'error'

interface ToolStoreState {
  status: ToolStatus
  /** 0-100 processing progress. */
  progress: number
  error: string | null
  resultBlob: Blob | null
  resultName: string | null
}

interface ToolStoreActions {
  /** A valid file has been picked; clear previous results. */
  selectFile: () => void
  /** Processing started; progress resets to 0. */
  start: () => void
  setProgress: (value: number) => void
  /** Processing finished with a downloadable blob. */
  finish: (blob: Blob, name: string) => void
  /** Processing failed with a user-friendly message. */
  fail: (message: string) => void
  /** Back to the initial state. */
  reset: () => void
}

const initialState: ToolStoreState = {
  status: 'idle',
  progress: 0,
  error: null,
  resultBlob: null,
  resultName: null,
}

export const useToolStore = create<ToolStoreState & ToolStoreActions>()((set) => ({
  ...initialState,

  selectFile: () =>
    set({ status: 'selected', progress: 0, error: null, resultBlob: null, resultName: null }),

  start: () => set({ status: 'processing', progress: 0, error: null }),

  setProgress: (value) =>
    set({ progress: Math.min(100, Math.max(0, Math.round(value))) }),

  finish: (blob, name) =>
    set({ status: 'done', resultBlob: blob, resultName: name, progress: 100 }),

  fail: (error) => set({ status: 'error', error, progress: 0 }),

  reset: () => set(initialState),
}))
